import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import { Alert } from '@/components/feedback/Alert'
import { useToastStack } from '@/components/feedback/toast-stack'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useCashFirm } from '@/features/cash/hooks'
import { CashFirmSelect } from '@/features/cash/shared'
import { HrPageHeader as PageHeader } from '@/features/hr/HrPageHeader'
import { cashRowLabel, periodLabel } from '@/lib/cash'
import { formatDateOnly, formatDecimal } from '@/lib/format'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { cn } from '@/lib/utils'
import { cashApi, type CashParallel } from '@/services/http/cash'

const t = messages.cash.parallel
const money = (v: number | null | undefined) =>
  v == null || Math.abs(v) < 0.005 ? '–' : formatDecimal(v, 0)

/** /nakit/paralel — Excel ile web haftalık tablosunun karşılaştırması (Faz 4, Excel'i bırakma). */
export function ParallelPage() {
  const { firms, firm, setFirm } = useCashFirm()
  const [file, setFile] = useState<File | null>(null)
  const [onlyDiff, setOnlyDiff] = useState(true)
  const { toast } = useToastStack()
  const run = useMutation({
    mutationFn: () => cashApi.parallel(firm!, file!),
    onError: (e) =>
      toast({
        type: 'error',
        title: messages.cash.planning.failed,
        description: toProblem(e).detail ?? undefined,
      }),
  })
  const r: CashParallel | undefined = run.data
  const rows = r ? r.rows.filter((x) => !onlyDiff || x.max_diff >= 1) : []

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t.title}
        description={t.description}
        actions={<CashFirmSelect firms={firms.data ?? []} firm={firm} onChange={setFirm} />}
      />
      <div className="bg-surface flex flex-wrap items-center gap-3 rounded-lg border p-3">
        <Input
          type="file"
          accept=".xlsx"
          className="w-80"
          aria-label={t.file}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Button disabled={!file || !firm} loading={run.isPending} onClick={() => run.mutate()}>
          {t.run}
        </Button>
        {r && (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={onlyDiff} onCheckedChange={(v) => setOnlyDiff(!!v)} />
            {t.onlyDiff}
          </label>
        )}
      </div>
      {r && (
        <>
          <Alert
            tone={r.same_start ? 'info' : 'warning'}
            title={
              r.same_start
                ? t.sameStart
                : t.differentStart(formatDateOnly(r.excel_start), formatDateOnly(r.web_start))
            }
          />
          <Alert
            tone={r.equal ? 'success' : 'danger'}
            title={r.equal ? t.equal : t.notEqual(formatDecimal(r.max_diff, 2))}
          />
          <div className="bg-surface overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="bg-surface sticky left-0 px-2 py-1 text-left">{t.row}</th>
                  <th className="px-2 py-1 text-right">{t.maxDiff}</th>
                  {r.periods.map((p) => (
                    <th key={p.start} className="px-2 py-1 text-right whitespace-nowrap">
                      {periodLabel(p).title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key} className="border-b align-top" data-key={row.key}>
                    <th
                      scope="row"
                      className="bg-surface sticky left-0 px-2 py-1 text-left font-normal"
                    >
                      {cashRowLabel(row.key)}
                    </th>
                    <td
                      className={cn(
                        'px-2 py-1 text-right tabular-nums',
                        row.max_diff >= 1 && 'text-danger font-semibold',
                      )}
                    >
                      {money(row.max_diff)}
                    </td>
                    {row.web.map((w, i) => {
                      const x = row.excel[i]
                      const diff = x != null && Math.abs(x - w) >= 1
                      return (
                        <td
                          key={r.periods[i]!.start}
                          className={cn(
                            'px-2 py-1 text-right tabular-nums',
                            diff && 'bg-danger/10',
                          )}
                        >
                          <span className="block">{money(x)}</span>
                          <span className="text-text-secondary block">{money(w)}</span>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-text-secondary text-xs">
            {t.excel} / {t.web}
          </p>
        </>
      )}
    </div>
  )
}
